import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {LoadingButton} from '@mui/lab';
import type {TFunction} from 'i18next';
import {useMutation, useQueryClient} from 'react-query';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {
    AutomationFlow,
    AutomationMigrationNote,
    AutomationMigrationNoteCode
} from 'crqlar-react-common/dist/models/automation/automation';
import {AutomationService} from 'crqlar-react-common/dist/services/automation/AutomationService';
import {getErrorMessage} from 'crqlar-react-common/dist/utils/http.util';

interface MigratedAutomationBannerProps {
    flow?: AutomationFlow;
    flowId?: string;
}

// An unknown code renders as itself rather than disappearing.
const noteSentence = (note: AutomationMigrationNote, t: TFunction): string => {
    const values = {nodeLabel: note.nodeLabel ?? '', detail: note.detail ?? ''};

    switch (note.code) {
        case AutomationMigrationNoteCode.AUDIENCE_SAVED:
            return t('The audience step "{{nodeLabel}}" now uses the saved audience "{{detail}}". Check its filter in the Audiences module.', values);

        case AutomationMigrationNoteCode.GET_PROFILE_REMOVED:
            return t('The step "{{nodeLabel}}" was removed; its values are now read from "{{detail}}".', values);

        case AutomationMigrationNoteCode.LOOP_REMOVED:
            return t('The loop "{{nodeLabel}}" was removed; the steps after the audience now run once per guest.', values);

        case AutomationMigrationNoteCode.LOOP_DONE_BRANCH_MOVED:
            return t('"{{nodeLabel}}" now follows the new "{{detail}}" step, which runs once everyone has been handled. Check it.', values);

        case AutomationMigrationNoteCode.LOOP_DONE_BRANCH_DISCONNECTED:
            return t('"{{nodeLabel}}" ran after the loop "{{detail}}" had finished; there is no such moment any more, so it is now disconnected: reconnect it or delete it.', values);

        case AutomationMigrationNoteCode.LOOP_REFERENCE_UNRESOLVED:
            return t('"{{nodeLabel}}" still references {{detail}}, which no longer exists; fix it by hand.', values);

        case AutomationMigrationNoteCode.GET_PROFILE_REFERENCE_UNRESOLVED:
            return t('The step "{{nodeLabel}}" was removed but its source ({{detail}}) could not be resolved; values that referenced it must be fixed by hand.', values);

        case AutomationMigrationNoteCode.AUDIENCE_FILTER_MISSING:
            return t('The audience node "{{nodeLabel}}" has no usable filter; select an audience.', values);

        default:
            return note.code;
    }
};

// Stays until dismissed: the migration notes are a checklist, not a status.
const MigratedAutomationBanner: React.FC<MigratedAutomationBannerProps> = ({flow, flowId}) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();

    const dismissMutation = useMutation({
        mutationFn: () => AutomationService.updateFlow(flowId!, {migrationNotes: []}),
        onSuccess: () => {
            void queryClient.invalidateQueries(['automation', flowId]);
        },
        onError: (error: unknown) => {
            enqueueSnackbar(
                getErrorMessage(error, t('The notes could not be dismissed')),
                {variant: 'error'}
            );
        }
    });

    const notes = flow?.migrationNotes ?? [];
    if (!flow?.migratedFromFlowId || !notes.length) return null;

    return (
        <Alert
            severity={'info'}
            sx={{
                flexShrink: 0,
                borderRadius: 0,
                alignItems: 'flex-start',
                '& .MuiAlert-action': {alignItems: 'center', pt: 0}
            }}
            action={(
                <LoadingButton
                    size={'small'}
                    color={'inherit'}
                    loading={dismissMutation.isLoading}
                    disabled={!flowId}
                    onClick={() => dismissMutation.mutate()}
                >
                    {t('Dismiss')}
                </LoadingButton>
            )}
        >
            <AlertTitle>{t('Verify this migrated automation')}</AlertTitle>

            <Typography variant={'body2'}>
                {flow.migratedFromFlowName
                    ? t('Created from "{{name}}". Check the points below, then set it live.', {name: flow.migratedFromFlowName})
                    : t('Check the points below, then set it live.')}
            </Typography>

            <Box component={'ul'} sx={{m: 0, mt: 0.5, pl: 2.5}}>
                {notes.map((note, index) => (
                    <Typography key={`${note.code}-${index}`} component={'li'} variant={'body2'}>
                        {noteSentence(note, t)}
                    </Typography>
                ))}
            </Box>
        </Alert>
    );
};

export default MigratedAutomationBanner;
